import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Moment } from 'moment';
import * as moment from 'moment';

import { HttpBase } from 'app/services/http-base.service';
import { GraphData } from 'app/buffer-graph/models/graph-data';
import { CompareItem } from 'app/buffer-graph/models/compare-item';
import { ChartData } from 'app/buffer-graph/models/chart-data';
import { ChartDataRow } from 'app/buffer-graph/models/chart-data-row';
import { ItemOrderHistoryBase } from 'app/buffer-graph/models/item-order-history/item-order-history-base';
import { ItemOrderHistory } from 'app/buffer-graph/models/item-order-history/item-order-history';
import { ItemOrderHistoryFresh } from 'app/buffer-graph/models/item-order-history/item-order-history-fresh';
import { BufferChangeHistory } from 'app/buffer-graph/models/buffer-change-history/buffer-change-history';
import { ItemAction } from 'app/buffer-graph/models/item-actions/item-action';
import { FreshItemActionHeader } from 'app/buffer-graph/models/item-actions/fresh-item-action-header';
import { FreshItemActionLine } from 'app/buffer-graph/models/item-actions/fresh-item-action-line';
import { BufferChangeReason } from 'app/buffer-graph/models/buffer-change-reasons/buffer-change-reason';
import { SupplierSchedule } from 'app/buffer-graph/models/suppliers-schedules/supplier-schedule';
import { StateInfo } from 'app/buffer-graph/models/assortment-structure/state-info';
import { BalanceStructure } from 'app/buffer-graph/models/assortment-structure/balance-structure';
import { ChangeBufferItem } from 'app/buffer-graph/models/chage-buffer-item';

type History = ItemOrderHistory | ItemOrderHistoryFresh;
type Action = ItemAction | FreshItemActionHeader;
type ReasonsResponse = { accessDenied: boolean, reasons: BufferChangeReason[] };
type SuppliersSchedulesResponse = { accessDenied: boolean, schedules: SupplierSchedule[] };
type AssortmentStructureResponse = { accessDenied: boolean, states: StateInfo[], balanceStructure: BalanceStructure };
type ChageBufferData = { accessDenied: boolean, item: ChangeBufferItem };
type ChageBufferResult = { accessDenied: boolean, success: boolean };

@Injectable()
export class BufferGraphService extends HttpBase {
    constructor(http: HttpClient, router: Router, translateService: TranslateService) {
        super(http, router, translateService);

        this.tabsresizeNotifier = new Subject<void>();
        this.onTabsResized = this.tabsresizeNotifier.asObservable();
    }

    private readonly tabsresizeNotifier: Subject<void>;

    private get fineDateFormat(): string { return 'YYYY.MM.DD'; }

    private get fineDateTimeFormat(): string { return 'YYYY.MM.DD HH:mm:ss'; }

    readonly onTabsResized: Observable<void>;

    notifyTabsResize = (): void => {
        this.tabsresizeNotifier.next();
    }

    getBufferGraphData = (itemId: number, daysCount?: number): Observable<{ accessDenied: boolean, graphData: GraphData }> => {
        return this.get<{ accessDenied: boolean, graphData: GraphData }>('/api/buffergraphdata/chartdata', { params: { itemId, daysCount } })
            .map(data => {
                if (!data || !data.graphData)
                    return data;

                const rows = data.graphData.chartData.rows.map(r => new ChartDataRow(
                    moment.utc(r.date),
                    r.bufferG,
                    r.bufferY,
                    r.bufferR,
                    r.safetyBuffer,
                    r.actionBuffer,
                    r.valueOut,
                    r.valueIn,
                    r.checkedBalance,
                    r.noData,
                    r.balance,
                    r.targetStockLevel,
                    r.packageValue,
                    r.replenishmentTime,
                    r.shelfQuantity,
                    r.actionSafetyBuffer,
                    r.maxQuantity,
                    r.maxBuffer,
                    r.minBuffer,
                    r.action,
                    r.season,
                    r.vacation,
                    r.holiday,
                    r.orderDay,
                    r.deliveryDay,
                    r.bufferGrowFixed,
                    r.bufferDecreaseFixed
                ));

                const chartData = new ChartData(
                    data.graphData.chartData.itemInfo,
                    rows,
                    data.graphData.chartData.min,
                    data.graphData.chartData.max,
                    data.graphData.chartData.rtMin,
                    data.graphData.chartData.rtMax,
                    moment.utc(data.graphData.chartData.startDate),
                    moment.utc(data.graphData.chartData.endDate),
                    data.graphData.chartData.visibleDaysCount,
                    data.graphData.chartData.showAction,
                    data.graphData.chartData.showSeason,
                    data.graphData.chartData.showVacation,
                    data.graphData.chartData.showHoliday,
                    data.graphData.chartData.showOrderDay,
                    data.graphData.chartData.showDeliveryDay,
                    data.graphData.chartData.showBufferGrowFixed,
                    data.graphData.chartData.showBufferDecreaseFixed,
                    data.graphData.chartData.isMaxQuantityDisplayable,
                    data.graphData.chartData.isMaxBufferDisplayable
                );

                data.graphData = new GraphData(data.graphData.settings, chartData);

                return data;
            });
    }

    getCompareData = (itemId: number): Observable<{ accessDenied: boolean, items: CompareItem[], cwhItemId: number }> => {
        return this.get<{ accessDenied: boolean, items: CompareItem[], cwhItemId: number }>('/api/buffergraphdata/comparedata', { params: { itemId } });
    }

    getItemOrderHistory = (itemId: number, statuses: { [index: number]: string }, fulfillments: { [index: number]: string }, daysCount?: number): Observable<{ accessDenied: boolean, isFresh: boolean, orders: History[], sku: string, warehouseCode: string, canSend: boolean }> => {
        const createFresh = (info: ItemOrderHistoryFresh, isCwh?: boolean): ItemOrderHistoryFresh => {
            return new ItemOrderHistoryFresh(info.orderId,
                moment.utc(info.date),
                moment.utc(info.planArriveDate),
                info.order,
                info.status,
                info.status > 0 ? statuses[info.status] : null,
                info.fulfillment,
                info.fulfillment > 0 ? fulfillments[info.fulfillment] : null,
                info.arrivalDate ? moment.utc(info.arrivalDate) : null,
                info.arrivedQuantity,
                info.supplierCode,
                info.supplierName,
                info.buffer,
                info.safetyStock,
                info.balance,
                info.inTransit,
                info.need,
                info.hasData,
                info.hasMoq,
                info.hasMoa,
                moment.utc(info.nextInputDate),
                moment.duration(info.orderTime),
                moment.duration(info.deliveryTime),
                info.lifeTime,
                info.weekSale,
                info.lastWeekSale,
                info.changedCoeff,
                info.baseBuffer,
                info.actionQnt,
                info.newMover,
                info.hasHolidayQnt,
                isCwh,
                info.crossDock ? createFresh(info.crossDock, true) : null);
        };
        const createNonFresh = (info: ItemOrderHistory, isCwh?: boolean): ItemOrderHistory => {
            return new ItemOrderHistory(info.crossDock ? info.orderId : info.orderId,
                moment.utc(info.date),
                moment.utc(info.planArriveDate),
                info.order,
                info.status,
                info.status > 0 ? statuses[info.status] : null,
                info.fulfillment,
                info.fulfillment > 0 ? fulfillments[info.fulfillment] : null,
                info.arrivalDate ? moment.utc(info.arrivalDate) : null,
                info.arrivedQuantity,
                info.supplierCode,
                info.supplierName,
                info.buffer,
                info.safetyStock,
                info.balance,
                info.inTransit,
                info.need,
                info.hasData,
                info.hasMoq,
                info.hasMoa,
                isCwh,
                info.crossDock ? createNonFresh(info.crossDock, true) : null);
        };
        return this.get<{ accessDenied: boolean, isFresh: boolean, orders: History[], sku: string, warehouseCode: string, canSend: boolean }>('/api/buffergraphdata/itemorderhistory', { params: { itemId, daysCount } }).map(data => {
            if (!data || !data.orders)
                return data;

            data.orders = data.orders.map(o => data.isFresh ? createFresh(o as ItemOrderHistoryFresh) : createNonFresh(o as ItemOrderHistory));

            return data;
        });
    }

    downloadItemOrderHistory = (columns: { header: string, field: string }[], mainRows: any[][], cwhRows: any[][], culture: string, mainSheetName: string, cwhSheetName: string): Observable<Blob> => {
        return this.post<Blob>('/api/buffergraphdata/downloaditemorderhistory', { columns, mainRows, cwhRows, culture, mainSheetName, cwhSheetName }, { responseType: 'blob' });
    }

    sendItemOrderHistory = (sku: string, warehouseCode: string, columns: { header: string, field: string }[], mainRows: any[][], cwhRows: any[][], culture: string, mainSheetName: string, cwhSheetName: string): Observable<{ success: boolean }> => {
        return this.post<{ success: boolean }>('/api/buffergraphdata/senditemorderhistory', { sku, warehouseCode, columns, mainRows, cwhRows, culture, mainSheetName, cwhSheetName });
    }

    getBufferChangeHistory = (itemId: number, daysCount?: number): Observable<{ accessDenied: boolean, changes: BufferChangeHistory[], sku: string, warehouseCode: string, canSend: boolean }> => {
        return this.get<{ accessDenied: boolean, changes: BufferChangeHistory[], sku: string, warehouseCode: string, canSend: boolean }>('/api/buffergraphdata/bufferchangehistory', { params: { itemId, daysCount } })
            .map(data => {
                if (data && data.changes)
                    data.changes = data.changes.map((c, i) => new BufferChangeHistory(`${i}`, moment.utc(c.date, this.fineDateFormat), c.parameter, c.reason, c.oldValue, c.newValue));//TODO: Check correctness of date

                return data;
            });
    }

    downloadBufferChangeHistory = (columns: { header: string, field: string }[], rows: any[][], culture: string, sheetName: string): Observable<Blob> => {
        return this.post<Blob>('/api/buffergraphdata/downloadbufferchangehistory', { columns, rows, culture, sheetName }, { responseType: 'blob' });
    }

    sendBufferChangeHistory = (sku: string, warehouseCode: string, columns: { header: string, field: string }[], rows: any[][], culture: string, sheetName: string): Observable<{ success: boolean }> => {
        return this.post<{ success: boolean }>('/api/buffergraphdata/sendbufferchangehistory', { sku, warehouseCode, columns, rows, culture, sheetName });
    }

    getItemActions = (itemId: number, showAll?: boolean): Observable<{ accessDenied: boolean, isFresh: boolean, actions: Action[], sku: string, warehouseCode: string, canSend: boolean }> => {
        let id = 0;
        const createFresh = (header: FreshItemActionHeader): FreshItemActionHeader => {
            let lineId = 0;
            return new FreshItemActionHeader(
                `${id++}`,
                header.actionSeason,
                header.status,
                header.purchaseDate,
                //moment.utc(header.purchaseDate, this.fineDateFormat),
                header.startDate,
                //moment.utc(header.startDate, this.fineDateFormat),
                header.endDate,
                //moment.utc(header.endDate, this.fineDateFormat),
                header.saleChangeRatio,
                header.saleIncrease,
                header.replenishment,
                header.actionOut,
                header.importDate,
                //moment.utc(header.importDate, this.fineDateFormat),
                header.documentNumber,
                header.lines
                    ? header.lines.map(l => new FreshItemActionLine(
                        `${lineId++}`,
                        l.orderDate,
                        //moment.utc(l.orderDate, this.fineDateFormat),
                        l.arrivalDate,
                        //moment.utc(l.arrivalDate, this.fineDateTimeFormat),
                        l.nextArrivalDate,
                        //moment.utc(l.nextArrivalDate, this.fineDateFormat),
                        l.buffer,
                        l.actionBuffer,
                        l.balance,
                        l.inTransit,
                        l.order,
                        l.actionOrder,
                    ))
                    : null);
        };
        const createNonFresh = (action: ItemAction): ItemAction => {
            return new ItemAction(
                `${id++}`,
                action.actionSeason,
                action.status,
                action.type,
                action.purchaseDate,
                //moment.utc(action.purchaseDate, this.fineDateFormat),
                action.startDate,
                //moment.utc(action.startDate, this.fineDateFormat),
                action.endDate,
                //moment.utc(action.endDate, this.fineDateFormat),
                action.saleChangeRatio,
                action.saleIncrease,
                action.safetyStock,
                action.averageSaleOverReplTime,
                action.bufferOld,
                action.bufferNew,
                action.bufferAfterAction,
                action.replenishment,
                action.actionOut,
                action.nestedAction,
                action.importDate,
                //moment.utc(action.importDate, this.fineDateFormat),
                action.documentNumber);
        };
        return this.get<{ accessDenied: boolean, isFresh: boolean, actions: Action[], sku: string, warehouseCode: string, canSend: boolean }>('/api/buffergraphdata/itemactions', { params: { itemId, showAll } }).map(data => {
            if (!data || !data.actions)
                return data;

            data.actions = data.actions.map(o => data.isFresh ? createFresh(o as FreshItemActionHeader) : createNonFresh(o as ItemAction));

            return data;
        });
    }

    downloadItemActions = (columns: { header: string, field: string }[], rows: any[][], culture: string, sheetName: string): Observable<Blob> => {
        return this.post<Blob>('/api/buffergraphdata/downloaditemactions', { columns, rows, culture, sheetName }, { responseType: 'blob' });
    }

    sendItemActions = (sku: string, warehouseCode: string, columns: { header: string, field: string }[], rows: any[][], culture: string, sheetName: string): Observable<{ success: boolean }> => {
        return this.post<{ success: boolean }>('/api/buffergraphdata/senditemactions', { sku, warehouseCode, columns, rows, culture, sheetName });
    }

    getBufferChangeReasons = (itemId: number, startDate: Moment, endDate?: Moment): Observable<ReasonsResponse> => {
        return this.get<ReasonsResponse>('/api/buffergraphdata/bufferchangereasons', { params: { itemId, startDate: startDate.toJSON(), endDate: endDate && endDate.toJSON() } }).map(data => {
                if (data && data.reasons)
                    data.reasons = data.reasons.map(r => new BufferChangeReason(moment.utc(r.date),
                        r.reason,
                        r.supplier,
                        r.lastOrderDate,
                        r.planDeliveryDate,
                        r.deliveryDate,
                        r.docInputDate,
                        r.initialOrder,
                        r.sentOrder,
                        r.arrivedOrder,
                        r.orderSchedule));

                return data;
            });
    }

    getSuppliersSchedules = (itemId: number): Observable<SuppliersSchedulesResponse> => {
        return this.get<SuppliersSchedulesResponse>('/api/buffergraphdata/suppliersschedules', { params: { itemId } }).map(data => {
            if (data && data.schedules)
                data.schedules = data.schedules.map((s, i) => new SupplierSchedule(`${i}`,
                    s.rang,
                    s.code,
                    s.name,
                    s.schedule,
                    s.isOrderDay,
                    s.moa,
                    s.moq));

            return data;
        });
    }

    getAssortmentStructure = (itemId: number): Observable<AssortmentStructureResponse> => {
        return this.get<AssortmentStructureResponse>('/api/buffergraphdata/assortmentstructure', { params: { itemId } }).map(data => {
            if (!data)
                return data;

            if (data.states)
                data.states = data.states.map(s => new StateInfo(s.state, s.count));

            if (data.balanceStructure) {
                const struct = data.balanceStructure;
                data.balanceStructure = new BalanceStructure(struct.zeroBalance,
                    struct.safetyStockZone,
                    struct.redZone,
                    struct.yellowZone,
                    struct.greenZone,
                    struct.overstockZone);
            }

            return data;
        });
    }

    getChangeBufferData = (itemId: number): Observable<ChageBufferData> => {
        return this.get<ChageBufferData>('/api/buffergraphdata/changebuffer', { params: { itemId } }).map(data => {
            if (data.item)
                data.item = new ChangeBufferItem(data.item.warehouseCode, data.item.warehouseName, data.item.sku, data.item.name, data.item.buffer);

            return data;
        });
    }

    changeBuffer = (itemId: number, buffer: number, reason: string): Observable<ChageBufferResult> => {
        return this.post<ChageBufferResult>('/api/buffergraphdata/changebuffer', { itemId, buffer, reason });
    }
}



// WEBPACK FOOTER //
// ./src/app/buffer-graph/services/buffer-graph.service.ts